import React from 'react';
import { Spinner } from 'reactstrap';
import utils from '../utils';

const supportedVendors: OrderHistory.SUPPORTED_VENDORS[] = ['amazon'];

export function makeOrdersFetchEffect(
  selectedCred: InstanceSlot | null | undefined,
  dataSetters: OrderHistory.DataSetters,
  requestSetters: OrderHistory.RequestSetters
): React.EffectCallback {
  return () => {
    const credentialState = composeCredentialState(selectedCred);

    dataSetters.setHasData(false);
    dataSetters.setFetchedAt(null);
    dataSetters.setWarningMessage('');
    dataSetters.setScrapeLimitDays(-1);
    dataSetters.setOrders([]);
    dataSetters.setProducts([]);

    if (credentialState.status === 'invalid') {
      requestSetters.db.setIsLoading(false);
      requestSetters.liveormemory.setIsLoading(false);
      dataSetters.setWarningMessage(credentialState.warningMessage || '');
      return;
    }

    const requestControllers: OrderHistory.RequestControllers = {
      db: new AbortController(),
      liveormemory: new AbortController(),
    };

    Promise.race([
      makeOrdersFetchPromise(
        'db',
        dataSetters,
        requestSetters,
        requestControllers,
        credentialState.credential
      ),
      makeOrdersFetchPromise(
        'liveormemory',
        dataSetters,
        requestSetters,
        requestControllers,
        credentialState.credential
      ),
    ]);

    // Trigger the abortion in useEffect's cleanup function
    return () => {
      requestControllers.db.abort();
      requestSetters.db.setIsLoading(false);
      requestSetters.db.setHadError(false);

      requestControllers.liveormemory.abort();
      requestSetters.liveormemory.setIsLoading(false);
      requestSetters.liveormemory.setHadError(false);
    };
  };
}

async function makeOrdersFetchPromise(
  cachePreference: OrderHistory.CachePreference,
  dataSetters: OrderHistory.DataSetters,
  requestSetters: OrderHistory.RequestSetters,
  requestControllers: OrderHistory.RequestControllers,
  selectedCred: InstanceSlot
) {
  const thisRequestsController = requestControllers[cachePreference];
  const thisRequestsSetters = requestSetters[cachePreference];
  thisRequestsSetters.setIsLoading(true);
  thisRequestsSetters.setHadError(false);

  try {
    const ordersOutput = await utils.getOrders(
      selectedCred,
      cachePreference,
      thisRequestsController.signal
    );
    thisRequestsSetters.setIsLoading(false);

    // Important to check.
    if (thisRequestsController.signal.aborted) return;

    const { response, errorMessage } = ordersOutput;

    if (errorMessage || !response?.success) {
      thisRequestsSetters.setHadError(true);
      return;
    }

    const fetchedAt = response?.fetchedAt ?? null;
    const warningMessage = response?.warningMessage ?? '';
    const scrapeLimitDays = response?.scrapeLimitDays ?? -1;
    const orders = response?.orders ?? null;
    const products = response?.products ?? null;

    if (orders && products) {
      dataSetters.setHasData(true);
      dataSetters.setFetchedAt(utils.constructDate(fetchedAt));
      dataSetters.setWarningMessage(warningMessage);
      dataSetters.setScrapeLimitDays(scrapeLimitDays);
      dataSetters.setOrders(orders);
      dataSetters.setProducts(products);

      if (cachePreference === 'liveormemory') {
        requestControllers.db.abort();
      }
    }
  } catch (e) {
    thisRequestsSetters.setIsLoading(false);

    if (!thisRequestsController.signal.aborted) {
      thisRequestsSetters.setHadError(true);
    }
  }
}

function composeCredentialState(
  credential: InstanceSlot | null | undefined
): OrderHistory.CredentialState {
  // `selectedCred` is null on page load.
  if (!credential)
    return {
      status: 'invalid',
      reason: 'credential_uninitialized',
      warningMessage: '',
    };

  // The instance is probably not configured if `.account` is falsy.
  if (!credential.account)
    return {
      status: 'invalid',
      reason: 'account_unconfigured',
      warningMessage:
        'It looks like this instance still needs to be set up. To configure it, please head to the Credentials page!',
    };

  if (credential.account.vaStatus !== 'active')
    return {
      status: 'invalid',
      reason: 'account_disabled',
      warningMessage:
        'It looks like this account is disabled. To configure it, please head to the Credentials page!',
    };

  if (!isSupportedVendor(supportedVendors, credential.vendor))
    return {
      status: 'invalid',
      reason: 'vendor_unsupported',
      warningMessage:
        'Sorry, this vendor is not supported by the order history feature yet.',
    };

  return {
    status: 'valid',
    credential,
  };
}

export function composeLoadingState(
  isLiveLoading: boolean,
  isDbLoading: boolean,
  hadLiveError: boolean,
  hasData: boolean,
  hadDbError: boolean,
  orders: VendorOrder[],
  products: OrderHistoryProduct[]
): OrderHistory.LoadingState {
  if (isLiveLoading) {
    return {
      status: 'loading',
      source: 'liveormemory',
    };
  } else if (isDbLoading) {
    return {
      status: 'loading',
      source: 'db',
    };
  } else if (hadLiveError) {
    return {
      status: 'error',
      source: 'liveormemory',
      errorMessage: 'There was an error scraping the latest orders.',
    };
  } else if (!hasData && hadDbError) {
    return {
      status: 'error',
      source: 'db',
      errorMessage: 'There was an error fetching orders from the database.',
    };
  } else if (hasData) {
    return {
      status: 'loaded',
      withOrders: orders.length > 0,
      withProducts: products.length > 0,
    };
  }

  return {
    status: 'initial',
  };
}

export function composeSubtitle(
  loadingState: OrderHistory.LoadingState,
  fetchedAt: Date | null
): string {
  const isLoadingDb =
    loadingState.status === 'loading' && loadingState.source === 'db';
  const isLoadingLive =
    loadingState.status === 'loading' && loadingState.source === 'liveormemory';

  const relativeFetchedAtTime = utils.relativeTimeFromDates(fetchedAt);
  const isFetchTimeValid = !!relativeFetchedAtTime;
  const isLoadingLiveAndFetchTimeIsInvalid = isLoadingLive && !isFetchTimeValid;

  if (isLoadingDb || isLoadingLiveAndFetchTimeIsInvalid) {
    return 'Loading...';
  }

  if (!isFetchTimeValid) {
    return '';
  }

  // This will be shown first with the scrape time for the db call,
  // and then with the scrape time for the live call when it finishes.
  return `Scraped ${relativeFetchedAtTime}`;
}

/**
 * Makes a cell element for the "Item ID" column.
 * @param item An order item.
 * @returns The composed <td/>.
 */
export function renderOrderItemIdCell(
  item: VendorOrderItem | OrderHistoryProduct
) {
  if (item.itemUrl) {
    return (
      <td>
        <a
          href={utils.makeDereferUrl(item.itemUrl)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.vendorProductId}
        </a>
      </td>
    );
  } else {
    return <td>{item.vendorProductId}</td>;
  }
}

/**
 * @returns The composed <span/>.
 */
export function renderDisclaimer(scrapeLimitDays: number) {
  if (scrapeLimitDays < 1) return;

  return (
    <span className="text-muted">{` (past ${scrapeLimitDays} days)`}</span>
  );
}

/**
 * @returns The composed <div/>.
 */
export function renderSpinner(loadingState: OrderHistory.LoadingState) {
  if (loadingState.status !== 'loading') return null;

  return (
    <Spinner size="sm" color="warning">
      {loadingState.source === 'liveormemory' ? 'Scraping...' : ''}
    </Spinner>
  );
}

const isSupportedVendor: SupersetIncludes<
  OrderHistory.SUPPORTED_VENDORS,
  VENDOR_NAME
> = (coll, el): el is OrderHistory.SUPPORTED_VENDORS => {
  return coll.includes(el as OrderHistory.SUPPORTED_VENDORS);
};
